import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import CustomButton from "components/common/CustomButton";
import ConfirmDialog from "components/common/ConfirmDialog";
import CustomScrollbars from "components/CustomScrollbars";
import UnsavedPlatformDraftsDialog from "components/userManagement/UnsavedPlatformDraftsDialog";
import ManagementPageShell from "components/management/ManagementPageShell";
import {
  MANAGEMENT_TABLE_AREA_SX,
  MANAGEMENT_TABS_SX,
} from "components/management/managementTheme";
import ClientRunHistoryPanel from "components/integrations-beta/ClientRunHistoryPanel";
import ClientStatusDialog from "components/integrations-beta/ClientStatusDialog";
import { FILTER_FIELD_H } from "components/integrations-beta/DateRangeSelect";
import FilesPanel from "components/integrations-beta/FilesPanel";
import IntegrationDetailsHeader from "components/integrations-beta/IntegrationDetailsHeader";
import RunActionsMenu, {
  type RunActionHandlers,
} from "components/integrations-beta/RunActionsMenu";
import RunClientStatus from "components/integrations-beta/RunClientStatus";
import RunNowControl from "components/integrations-beta/RunNowControl";
import RunsFilterBar, {
  type RunsFilters,
} from "components/integrations-beta/RunsFilterBar";
import RunsHistoryTable from "components/integrations-beta/RunsHistoryTable";
import RunsPagination from "components/integrations-beta/RunsPagination";
import SettingsPanel from "components/integrations-beta/SettingsPanel";
import StepsFilterBar, {
  type StepsFilters,
} from "components/integrations-beta/StepsFilterBar";
import StepExecutionsTable from "components/integrations-beta/StepExecutionsTable";
import StepExecutionDetails from "components/integrations-beta/StepExecutionDetails";
import { isRunExecuting } from "features/integrationsBeta/clientRunStatus";
import {
  headerDataFromClientPortal,
  headerDataFromDetail,
} from "features/integrationsBeta/integrationHeaderData";
import {
  IntegrationRunListItem,
  IntegrationRunSortField,
  StepExecutionItem,
  StepExecutionSortField,
  useGetClientPortalIntegrationQuery,
  useGetIntegrationDetailQuery,
  useGetIntegrationRunDetailQuery,
  useGetIntegrationRunsQuery,
  useRerunIntegrationRunMutation,
} from "features/integrationsBeta/integrationsBeta";
import {
  useGetClientRunStatusesQuery,
  useGetIntegrationStepStagesQuery,
  useGetRunStatusesQuery,
  useGetRunTriggerTypesQuery,
  usePlatformEnumLabels,
} from "features/integrationsBeta/platformEnums";
import {
  filterStepExecutions,
  sortStepExecutions,
} from "features/integrationsBeta/stepExecutions";
import {
  ALL_TIMEZONE_DISPLAY_MODES,
  CLIENT_FACING_TIMEZONE_DISPLAY_MODES,
  resolveDisplayZone,
  useTimezoneDisplayMode,
} from "features/integrationsBeta/timezoneDisplay";
import { useInFlightPolling } from "features/integrationsBeta/useInFlightPolling";
import {
  type IntegrationTab,
  useIntegrationDetailsUrlState,
} from "features/integrationsBeta/useIntegrationDetailsUrlState";
import { usePlatformRole } from "features/integrationsBeta/usePlatformRole";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import useAppNavigate from "hooks/useAppNavigate";
import { formatIsoDateTime } from "utils/dateUtils";
import { formatApiError } from "utils/formatApiError";
import { cycleSortField, type SortOrder } from "utils/sortUtils";
import Layout from "../components/Layout";

type DrillLevel = "runs" | "steps" | "step-details";

const DEFAULT_PAGE_SIZE = 10;

export default function IntegrationBetaDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useAppNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  // canView = any internal role. Without it (a client user, or an admin
  // previewing one in Client View) the page is the client's: one History tab
  // in the client shape, no internal detail requested at all.
  const { canEdit, canRun, canView } = usePlatformRole();
  const { statusLabel, stageLabel, triggerLabel, clientStatusLabel } =
    usePlatformEnumLabels({ skipInternal: !canView });
  const { data: runStatuses } = useGetRunStatusesQuery(undefined, {
    skip: !canView,
  });
  const { data: runTriggerTypes } = useGetRunTriggerTypesQuery(undefined, {
    skip: !canView,
  });
  const { data: stepStages } = useGetIntegrationStepStagesQuery(undefined, {
    skip: !canView,
  });
  const { data: clientStatuses } = useGetClientRunStatusesQuery();

  const integrationId = id ? parseInt(id, 10) : NaN;
  const integrationIdValid = !Number.isNaN(integrationId);

  // Read once, lazily: User Management navigates here with
  // location.state.fromUserManagement so Back returns there.
  const locationState = location.state as {
    fromUserManagement?: boolean;
  } | null;

  const [selectedStepExecutionId, setSelectedStepExecutionId] = useState<
    number | null
  >(null);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [runsSortBy, setRunsSortBy] = useState<
    IntegrationRunSortField | undefined
  >();
  const [runsSortOrder, setRunsSortOrder] = useState<SortOrder>("asc");
  const [runsFilters, setRunsFilters] = useState<RunsFilters>({});
  const [stepsSortBy, setStepsSortBy] = useState<
    StepExecutionSortField | undefined
  >();
  const [stepsSortOrder, setStepsSortOrder] = useState<SortOrder>("asc");
  const [stepsFilters, setStepsFilters] = useState<StepsFilters>({});

  // Servicing a run (write tier): the dialog / confirmation targets.
  const [clientStatusTarget, setClientStatusTarget] =
    useState<IntegrationRunListItem | null>(null);
  const [rerunTarget, setRerunTarget] = useState<IntegrationRunListItem | null>(
    null,
  );
  const [rerunRun, { isLoading: isRerunning }] =
    useRerunIntegrationRunMutation();

  // A client (or an admin previewing one) chooses Local / UTC only: for them
  // "Client" would mean themselves.
  const timezoneModes = canView
    ? ALL_TIMEZONE_DISPLAY_MODES
    : CLIENT_FACING_TIMEZONE_DISPLAY_MODES;
  const [timezoneMode, setTimezoneMode] = useTimezoneDisplayMode(timezoneModes);
  // Pending Settings edits guard tab switches and the Back button.
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<
    null | { kind: "tab"; tab: IntegrationTab } | { kind: "back" }
  >(null);
  // Set the moment the integration is deleted, so this page's queries go quiet
  // while the cache invalidation settles and React unmounts it - otherwise
  // they refetch a row that no longer exists.
  const [deleted, setDeleted] = useState(false);

  const {
    data: integration,
    isFetching: isFetchingIntegration,
    isError: integrationLoadFailed,
    error: integrationError,
  } = useGetIntegrationDetailQuery(integrationId, {
    skip: !integrationIdValid || deleted || !canView,
  });
  // The client's header comes from the client-portal endpoint: nothing
  // internal in it, and a 404 for another client's integration.
  const {
    data: clientIntegration,
    isFetching: isFetchingClientIntegration,
    isError: clientIntegrationLoadFailed,
    error: clientIntegrationError,
  } = useGetClientPortalIntegrationQuery(integrationId, {
    skip: !integrationIdValid || deleted || canView,
  });
  const headerData = useMemo(
    () =>
      integration
        ? headerDataFromDetail(integration)
        : clientIntegration
          ? headerDataFromClientPortal(clientIntegration)
          : null,
    [integration, clientIntegration],
  );
  const isFetchingHeader = canView
    ? isFetchingIntegration
    : isFetchingClientIntegration;
  const headerLoadFailed = canView
    ? integrationLoadFailed
    : clientIntegrationLoadFailed;
  const headerLoadError = canView ? integrationError : clientIntegrationError;

  // The Files tab browses the managed integration archive - only pipelines
  // with platform.storage.* steps write there, so others hide the tab.
  const hasIntegrationStorage = (integration?.steps ?? []).some((step) =>
    step.step_key?.startsWith("platform.storage."),
  );
  const allowedTabs = useMemo<IntegrationTab[]>(
    () =>
      canView
        ? [
            "history",
            "client-history",
            ...(hasIntegrationStorage ? (["files"] as const) : []),
            "settings",
          ]
        : ["client-history"],
    [canView, hasIntegrationStorage],
  );
  // Tab and selected run live in the URL (?tab=, ?run=) so the two histories
  // can deep-link to each other and a run can be shared by link.
  const {
    tab: integrationTab,
    runId,
    setTab,
    setRunId,
    openRunIn,
  } = useIntegrationDetailsUrlState(
    allowedTabs,
    canView ? "history" : "client-history",
  );

  // Zone the History timestamps render in; Client mode = the integration's
  // own timezone (one zone for the whole page).
  const displayZone = resolveDisplayZone(timezoneMode, headerData?.timezone);

  const goBack = () =>
    locationState?.fromUserManagement
      ? navigate(-1)
      : navigate("/integrations-beta");

  const runsQueryArgs = useMemo(
    () => ({
      integration_id: integrationId,
      page,
      page_size: pageSize,
      ...runsFilters,
      ...(runsSortBy ? { sort_by: runsSortBy, sort_order: runsSortOrder } : {}),
    }),
    [integrationId, page, pageSize, runsFilters, runsSortBy, runsSortOrder],
  );

  const [runsPolling, setRunsPollingActive] = useInFlightPolling();
  const {
    data: runsData,
    isLoading: isLoadingRuns,
    isFetching: isFetchingRuns,
  } = useGetIntegrationRunsQuery(runsQueryArgs, {
    skip: !integrationIdValid || deleted || !canView,
    pollingInterval: runsPolling,
  });

  const runs = runsData?.results ?? [];
  const totalRuns = runsData?.count ?? 0;
  const anyRunExecuting = runs.some(isRunExecuting);
  useEffect(
    () => setRunsPollingActive(anyRunExecuting),
    [anyRunExecuting, setRunsPollingActive],
  );
  // A background poll must not dim the rows every fifteen seconds.
  const runsBusy = isFetchingRuns && runsPolling === 0;

  const inHistory = integrationTab === "history" && canView;
  const [detailPolling, setDetailPollingActive] = useInFlightPolling();
  const {
    currentData: runDetail,
    isFetching: isFetchingRunDetail,
    isError: runDetailFailed,
    error: runDetailError,
  } = useGetIntegrationRunDetailQuery(runId ?? 0, {
    skip: !inHistory || runId === null || deleted,
    pollingInterval: detailPolling,
  });
  const detailExecuting = !!runDetail && isRunExecuting(runDetail);
  useEffect(
    () => setDetailPollingActive(detailExecuting),
    [detailExecuting, setDetailPollingActive],
  );

  // The selected run is derived from the URL: the detail once loaded (it
  // carries everything the list item does), else the list row while it loads.
  const selectedRun: IntegrationRunListItem | null = inHistory
    ? (runDetail ?? runs.find((run) => run.id === runId) ?? null)
    : null;
  const drillLevel: DrillLevel =
    !inHistory || runId === null
      ? "runs"
      : selectedStepExecutionId !== null
        ? "step-details"
        : "steps";

  // Deep-link guard: a run that does not exist or belongs to another
  // integration drops the parameter instead of showing a foreign drill-down.
  useEffect(() => {
    if (!inHistory || runId === null) return;
    if (runDetailFailed) {
      const status = (runDetailError as FetchBaseQueryError | undefined)
        ?.status;
      dispatch(
        pushNotification({
          severity: "error",
          message:
            status === 404
              ? "Run not found."
              : formatApiError(
                  (runDetailError as FetchBaseQueryError | undefined)?.data,
                  "Could not load the run.",
                ),
        }),
      );
      setRunId(null);
      return;
    }
    if (runDetail && runDetail.integration !== integrationId) {
      dispatch(
        pushNotification({
          severity: "error",
          message: "That run belongs to a different integration.",
        }),
      );
      setRunId(null);
    }
  }, [
    inHistory,
    runId,
    runDetail,
    runDetailFailed,
    runDetailError,
    integrationId,
    dispatch,
    setRunId,
  ]);

  const steps: StepExecutionItem[] = useMemo(() => {
    if (!runDetail) return [];
    const filtered = filterStepExecutions(
      runDetail.step_executions,
      stepsFilters,
    );
    return sortStepExecutions(filtered, stepsSortBy, stepsSortOrder);
  }, [runDetail, stepsFilters, stepsSortBy, stepsSortOrder]);

  const selectedStep =
    selectedStepExecutionId != null
      ? (steps.find((s) => s.id === selectedStepExecutionId) ?? null)
      : null;

  const resetStepsView = () => {
    setSelectedStepExecutionId(null);
    setStepsSortBy(undefined);
    setStepsSortOrder("asc");
    setStepsFilters({});
  };

  const goToRuns = () => {
    resetStepsView();
    setRunId(null);
  };

  const goToSteps = () => {
    setSelectedStepExecutionId(null);
  };

  const handleDrillBack = () => {
    if (drillLevel === "step-details") {
      goToSteps();
      return;
    }
    if (drillLevel === "steps") {
      goToRuns();
    }
  };

  const openRun = (nextRunId: number) => {
    resetStepsView();
    setRunId(nextRunId);
  };

  const handleSelectRun = (run: IntegrationRunListItem) => openRun(run.id);

  const handleSelectStep = (step: StepExecutionItem) => {
    setSelectedStepExecutionId(step.id);
  };

  const handleRunsSortChange = (field: IntegrationRunSortField) => {
    const next = cycleSortField(runsSortBy, field, runsSortOrder);
    setRunsSortBy(next.sortBy);
    setRunsSortOrder(next.sortOrder);
    setPage(1);
  };

  const handleStepsSortChange = (field: StepExecutionSortField) => {
    const next = cycleSortField(stepsSortBy, field, stepsSortOrder);
    setStepsSortBy(next.sortBy);
    setStepsSortOrder(next.sortOrder);
  };

  const handleRunsFiltersChange = (next: RunsFilters) => {
    setRunsFilters(next);
    setPage(1);
  };

  const runActions: RunActionHandlers = {
    onSetClientStatus: setClientStatusTarget,
    onRerun: setRerunTarget,
    onOpenClientHistory: (clientRunId) =>
      openRunIn("client-history", clientRunId),
    onOpenRun: openRun,
  };

  const handleRerun = async () => {
    if (!rerunTarget) return;
    try {
      await rerunRun({ runId: rerunTarget.id, integrationId }).unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message: "Re-run started. It will appear in the history shortly.",
        }),
      );
      setPage(1);
    } catch (err) {
      dispatch(
        pushNotification({
          severity: "error",
          message: formatApiError(
            (err as { data?: unknown })?.data,
            "Could not start the re-run.",
          ),
        }),
      );
    } finally {
      setRerunTarget(null);
    }
  };

  // Deleting an integration makes its URL permanently 404, which a stale
  // bookmark or a second tab can still hit.
  const handleIntegrationDeleted = () => {
    setSettingsDirty(false);
    setPendingLeave(null);
    setDeleted(true);
    // replace: the dead detail URL must not stay reachable via Back/Forward.
    navigate("/integrations-beta", { replace: true });
  };

  if (!integrationIdValid) {
    return (
      <Layout
        title="Integration Details"
        content="Integration Details"
        noScroll
      >
        <ManagementPageShell title="Integration Details">
          <Typography color="error">Invalid integration id.</Typography>
        </ManagementPageShell>
      </Layout>
    );
  }

  if (!deleted && headerLoadFailed && !headerData) {
    const status = (headerLoadError as FetchBaseQueryError | undefined)?.status;
    const message = !canView
      ? "This integration isn't available in your account."
      : status === 403
        ? "You do not have access to this integration."
        : "This integration no longer exists.";
    return (
      <Layout
        title="Integration Details"
        content="Integration Details"
        noScroll
      >
        <ManagementPageShell title="Integration Details">
          <Typography color="error" gutterBottom>
            {message}
          </Typography>
          <CustomButton
            fullWidth={false}
            onClick={() => navigate("/integrations-beta")}
          >
            Back to integrations
          </CustomButton>
        </ManagementPageShell>
      </Layout>
    );
  }

  return (
    <Layout title="Integration Details" content="Integration Details" noScroll>
      <ManagementPageShell
        title="Integration Details"
        headerExtra={
          <CustomButton
            fullWidth={false}
            onClick={() => {
              if (integrationTab === "settings" && settingsDirty) {
                setPendingLeave({ kind: "back" });
                return;
              }
              goBack();
            }}
          >
            Back
          </CustomButton>
        }
        beforePanel={
          headerData ? (
            <IntegrationDetailsHeader
              data={headerData}
              statusLabel={statusLabel}
              clientStatusLabel={clientStatusLabel}
              timezoneMode={timezoneMode}
              onTimezoneModeChange={setTimezoneMode}
              timezoneModes={timezoneModes}
            />
          ) : undefined
        }
      >
        {isFetchingHeader && !headerData ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexGrow={1}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs
              value={integrationTab}
              onChange={(_, value: IntegrationTab) => {
                if (
                  integrationTab === "settings" &&
                  value !== "settings" &&
                  settingsDirty
                ) {
                  setPendingLeave({ kind: "tab", tab: value });
                  return;
                }
                setTab(value);
              }}
              variant={isMobileLayout ? "scrollable" : "standard"}
              scrollButtons={isMobileLayout ? "auto" : false}
              allowScrollButtonsMobile
              sx={{
                flexShrink: 0,
                borderBottom: 1,
                borderColor: "divider",
                mb: 1.5,
                ...MANAGEMENT_TABS_SX,
              }}
            >
              {/* A client gets one tab, in the client shape, under the plain
                  name - the internal history is never reachable for them. */}
              {canView ? (
                <Tab label="History" value="history" />
              ) : (
                <Tab label="History" value="client-history" />
              )}
              {canView && <Tab label="Client history" value="client-history" />}
              {canView && hasIntegrationStorage && (
                <Tab label="Files" value="files" />
              )}
              {canView && <Tab label="Settings" value="settings" />}
            </Tabs>

            {inHistory && (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {drillLevel !== "runs" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                      minHeight: 36,
                      flexShrink: 0,
                    }}
                  >
                    <Tooltip title="Back">
                      <IconButton
                        size="small"
                        onClick={handleDrillBack}
                        aria-label="Go back"
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Breadcrumbs
                      separator={<NavigateNextIcon fontSize="small" />}
                      aria-label="run drill-down breadcrumb"
                      sx={{ minWidth: 0 }}
                    >
                      <MuiLink
                        component="button"
                        underline="hover"
                        color="primary"
                        onClick={goToRuns}
                        sx={{ fontWeight: 600 }}
                      >
                        Runs history
                      </MuiLink>

                      {selectedRun &&
                        (drillLevel === "steps" ? (
                          <Typography
                            color="text.primary"
                            fontWeight={600}
                            noWrap
                          >
                            Run ·{" "}
                            {formatIsoDateTime(
                              selectedRun.started_at,
                              displayZone,
                            )}
                          </Typography>
                        ) : (
                          <MuiLink
                            component="button"
                            underline="hover"
                            color="primary"
                            onClick={goToSteps}
                            sx={{ fontWeight: 600 }}
                            noWrap
                          >
                            Run ·{" "}
                            {formatIsoDateTime(
                              selectedRun.started_at,
                              displayZone,
                            )}
                          </MuiLink>
                        ))}

                      {drillLevel === "step-details" && selectedStep && (
                        <Typography
                          color="text.primary"
                          fontWeight={600}
                          noWrap
                        >
                          Step · {selectedStep.step_name}
                        </Typography>
                      )}
                    </Breadcrumbs>

                    {selectedRun && (
                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        sx={{ ml: 1, flexShrink: 0 }}
                      >
                        {selectedRun.client_visible && (
                          <Typography variant="caption" color="text.secondary">
                            Client sees
                          </Typography>
                        )}
                        <RunClientStatus
                          run={selectedRun}
                          clientStatusLabel={clientStatusLabel}
                        />
                      </Stack>
                    )}

                    <Box sx={{ flexGrow: 1 }} />
                    {selectedRun?.logs_url && (
                      <Tooltip title="Open this run's log lines in CloudWatch">
                        <Button
                          component="a"
                          href={selectedRun.logs_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          startIcon={<OpenInNewIcon fontSize="small" />}
                          sx={{
                            flexShrink: 0,
                            height: FILTER_FIELD_H,
                            fontWeight: "bold",
                            whiteSpace: "nowrap",
                          }}
                        >
                          View logs
                        </Button>
                      </Tooltip>
                    )}
                    {selectedRun && (
                      <RunActionsMenu
                        run={selectedRun}
                        canEdit={canEdit}
                        canRun={canRun}
                        statusLabel={statusLabel}
                        {...runActions}
                      />
                    )}
                  </Box>
                )}

                {drillLevel === "runs" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      mb: 1.5,
                      flexShrink: 0,
                    }}
                  >
                    {/* The filter bar wraps inside its own box, so Run now
                        stays on the first line however many filters there are. */}
                    <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
                      <RunsFilterBar
                        filters={runsFilters}
                        onChange={handleRunsFiltersChange}
                        statuses={runStatuses ?? []}
                        triggers={runTriggerTypes ?? []}
                        clientStatuses={clientStatuses ?? []}
                        showTrigger={canView}
                      />
                    </Box>
                    {canRun && integration && (
                      <Box sx={{ flexShrink: 0 }}>
                        <RunNowControl
                          integration={integration}
                          onTriggered={() => setPage(1)}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {drillLevel === "steps" && (
                  <StepsFilterBar
                    filters={stepsFilters}
                    onChange={setStepsFilters}
                    stages={stepStages ?? []}
                    statuses={runStatuses ?? []}
                  />
                )}

                <Box
                  sx={{ ...MANAGEMENT_TABLE_AREA_SX, flexDirection: "column" }}
                >
                  {drillLevel === "runs" && (
                    <RunsHistoryTable
                      runs={runs}
                      isLoading={isLoadingRuns}
                      isFetching={runsBusy}
                      selectedRunId={runId}
                      onSelectRun={handleSelectRun}
                      sortBy={runsSortBy}
                      sortOrder={runsSortOrder}
                      onSortChange={handleRunsSortChange}
                      statusLabel={statusLabel}
                      triggerLabel={triggerLabel}
                      clientStatusLabel={clientStatusLabel}
                      timezoneMode={timezoneMode}
                      displayZone={displayZone}
                      canEdit={canEdit}
                      canRun={canRun}
                      actions={runActions}
                      pagination={
                        <RunsPagination
                          page={page}
                          pageSize={pageSize}
                          total={totalRuns}
                          disabled={runsBusy}
                          onPageChange={setPage}
                          onPageSizeChange={(limit) => {
                            setPageSize(limit);
                            setPage(1);
                          }}
                        />
                      }
                    />
                  )}

                  {drillLevel === "steps" &&
                    (isFetchingRunDetail && !runDetail ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{ p: 6, flex: 1 }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <StepExecutionsTable
                        steps={steps}
                        selectedStepExecutionId={selectedStepExecutionId}
                        onSelectStep={handleSelectStep}
                        sortBy={stepsSortBy}
                        sortOrder={stepsSortOrder}
                        onSortChange={handleStepsSortChange}
                        statusLabel={statusLabel}
                        stageLabel={stageLabel}
                        timezoneMode={timezoneMode}
                        displayZone={displayZone}
                      />
                    ))}

                  {drillLevel === "step-details" && selectedStep && (
                    <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
                      <Box sx={{ p: 2, pr: 2.5 }}>
                        <StepExecutionDetails
                          step={selectedStep}
                          statusLabel={statusLabel(selectedStep.status)}
                          stageLabel={stageLabel(selectedStep.stage)}
                          timezoneMode={timezoneMode}
                          displayZone={displayZone}
                        />
                      </Box>
                    </CustomScrollbars>
                  )}
                </Box>
              </Box>
            )}

            {integrationTab === "client-history" && !deleted && (
              <ClientRunHistoryPanel
                integrationId={integrationId}
                timezoneMode={timezoneMode}
                displayZone={displayZone}
                internalLinks={canView}
                selectedRunId={runId}
                onSelectRun={setRunId}
                onOpenInternalRun={(nextRunId) => {
                  resetStepsView();
                  openRunIn("history", nextRunId);
                }}
                clientStatusLabel={clientStatusLabel}
              />
            )}

            {integrationTab === "files" &&
              hasIntegrationStorage &&
              integration && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <FilesPanel
                    integrationId={integration.id}
                    integrationName={integration.name}
                  />
                </Box>
              )}

            {integrationTab === "settings" && integration && (
              // trackOffsetTop clears the sticky sub-tab bar; the content's
              // right padding keeps the track off the section cards.
              <CustomScrollbars
                sx={{ flex: 1, minHeight: 0 }}
                trackOffsetTop={56}
              >
                <Box sx={{ pb: 1, pr: 1.5 }}>
                  <SettingsPanel
                    integration={integration}
                    onDirtyChange={setSettingsDirty}
                    onDeleted={handleIntegrationDeleted}
                  />
                </Box>
              </CustomScrollbars>
            )}
          </>
        )}

        <ClientStatusDialog
          open={clientStatusTarget !== null}
          run={clientStatusTarget}
          onClose={() => setClientStatusTarget(null)}
          displayZone={displayZone}
          statusLabel={statusLabel}
          clientStatusLabel={clientStatusLabel}
        />

        <ConfirmDialog
          open={rerunTarget !== null}
          title="Re-run this run?"
          message="A new run will start with the same trigger data as this run. Re-runs are internal: the client keeps seeing the original run, with its current client status, until you set it."
          confirmLabel="Re-run"
          loading={isRerunning}
          onConfirm={handleRerun}
          onCancel={() => setRerunTarget(null)}
        />

        <UnsavedPlatformDraftsDialog
          open={pendingLeave != null}
          onClose={() => setPendingLeave(null)}
          rows={[]}
          title="Unsaved integration settings"
          intro="You have unsaved changes in Settings. Stay to review and save them, or discard them and continue."
          onDiscardAndContinue={() => {
            const leave = pendingLeave;
            setPendingLeave(null);
            setSettingsDirty(false);
            if (!leave) return;
            // Leaving unmounts SettingsPanel; the pending store dies with it.
            if (leave.kind === "tab") setTab(leave.tab);
            else goBack();
          }}
        />
      </ManagementPageShell>
    </Layout>
  );
}
